import { useCallback, useEffect, useRef, useState } from "react";
import api from "../../services/api";

const POLL_INTERVAL_MS = 3000;

/**
 * Estado de Email Marketing — integrado com o backend real
 * (RAVENA-BACK: /email-marketing/*).
 *
 * Módulo GLOBAL — não é escopado por projeto: listas, contatos e
 * campanhas são os mesmos para o app inteiro, independente de qual
 * projeto o usuário tem selecionado (e independente do ambiente —
 * dev.vidal-app.com e vidal-app.com compartilham a mesma base).
 *
 * Campanhas em status `queued`/`sending` são acompanhadas via polling
 * simples, já que o disparo real acontece de forma assíncrona no WORKER
 * (via Kafka).
 */
export default function useEmailMarketingData() {
  const [campaigns, setCampaigns] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [lists, setLists] = useState([]);
  const [loading, setLoading] = useState(true);

  const pollRef = useRef(null);

  const fetchLists = useCallback(async () => {
    const res = await api.get(`/email-marketing/lists`);
    setLists(res.data.data || []);
  }, []);

  const fetchContacts = useCallback(async () => {
    const res = await api.get(`/email-marketing/contacts`);
    setContacts(res.data.data || []);
  }, []);

  const fetchCampaigns = useCallback(async () => {
    const res = await api.get(`/email-marketing/campaigns`);
    setCampaigns(res.data.data || []);
  }, []);

  // Carga inicial
  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        await Promise.all([fetchLists(), fetchContacts(), fetchCampaigns()]);
      } catch (error) {
        console.error("Erro ao carregar dados de email marketing:", error);
      } finally {
        setLoading(false);
      }
    })();
  }, [fetchLists, fetchContacts, fetchCampaigns]);

  // Polling enquanto houver campanha em processamento assíncrono no WORKER
  // (também quando uma campanha agendada está prestes a disparar — próximos
  // 5 min — para refletir a transição automática feita pelo cron).
  useEffect(() => {
    const soonThreshold = Date.now() + 5 * 60 * 1000;
    const hasPending = campaigns.some((c) => {
      if (["queued", "sending"].includes(c.status)) return true;
      if (c.status === "scheduled" && c.scheduledAt) {
        return new Date(c.scheduledAt).getTime() <= soonThreshold;
      }
      return false;
    });

    if (hasPending && !pollRef.current) {
      pollRef.current = setInterval(() => {
        fetchCampaigns();
      }, POLL_INTERVAL_MS);
    }

    if (!hasPending && pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }

    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, [campaigns, fetchCampaigns]);

  /* ---------------- Lists ---------------- */
  const createList = useCallback(async (name) => {
    const res = await api.post(`/email-marketing/lists`, { name });
    const newList = res.data.data;
    setLists((prev) => [...prev, newList]);
    return newList;
  }, []);

  /**
   * Exclui uma lista. O backend desanexa o listId de todos os contatos e
   * limpa a referência em campanhas editáveis. Com `removeContacts: true`,
   * contatos que ficam sem nenhuma lista são marcados como removidos.
   */
  const deleteList = useCallback(
    async (listId, { removeContacts = false } = {}) => {
      const res = await api.delete(`/email-marketing/lists/${listId}`, {
        params: { removeContacts },
      });
      setLists((prev) => prev.filter((l) => l.id !== listId));
      // Contatos mudam (listIds/status) e campanhas editáveis podem ter
      // perdido a referência à lista — refresca ambos.
      await Promise.all([fetchContacts(), fetchCampaigns()]);
      return res.data.data;
    },
    [fetchContacts, fetchCampaigns]
  );

  /* ---------------- Contacts ---------------- */
  const addContact = useCallback(
    async ({ name, email, listIds }) => {
      const res = await api.post(`/email-marketing/contacts`, { name, email, listIds });
      await fetchContacts();
      return res.data.data;
    },
    [fetchContacts]
  );

  const removeContact = useCallback(async (contactId) => {
    await api.patch(`/email-marketing/contacts/${contactId}`);
    setContacts((prev) => prev.map((c) => (c.id === contactId ? { ...c, status: "removed" } : c)));
  }, []);

  /**
   * Remove vários contatos de uma vez (soft-delete). Retorna
   * { removed, failures } do backend.
   */
  const removeContacts = useCallback(async (contactIds) => {
    const res = await api.post(`/email-marketing/contacts/batch-remove`, { contactIds });
    const ids = new Set(contactIds);
    setContacts((prev) => prev.map((c) => (ids.has(c.id) ? { ...c, status: "removed" } : c)));
    return res.data.data;
  }, []);

  /**
   * Importa contatos a partir de uma planilha .xlsx/.csv (parsing real no
   * backend via exceljs). Retorna { imported, skipped, errors }.
   */
  const importContacts = useCallback(
    async (listId, file) => {
      const formData = new FormData();
      formData.append("file", file);
      if (listId) formData.append("listId", listId);

      const res = await api.post(`/email-marketing/contacts/import`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      await fetchContacts();
      return res.data.data;
    },
    [fetchContacts]
  );

  /* ---------------- Campaigns ---------------- */
  const createCampaign = useCallback(async (data) => {
    const res = await api.post(`/email-marketing/campaigns`, data);
    const newCampaign = res.data.data;
    setCampaigns((prev) => [newCampaign, ...prev]);
    return newCampaign;
  }, []);

  const updateCampaign = useCallback(async (campaignId, patch) => {
    await api.patch(`/email-marketing/campaigns/${campaignId}`, patch);
    setCampaigns((prev) => prev.map((c) => (c.id === campaignId ? { ...c, ...patch } : c)));
  }, []);

  const deleteCampaign = useCallback(async (campaignId) => {
    await api.delete(`/email-marketing/campaigns/${campaignId}`);
    setCampaigns((prev) => prev.filter((c) => c.id !== campaignId));
  }, []);

  /**
   * Dispara a campanha (assíncrono via Kafka/WORKER). Marca `queued`
   * localmente de imediato e deixa o polling assumir a partir daí.
   */
  const sendCampaign = useCallback(
    async (campaignId) => {
      setCampaigns((prev) => prev.map((c) => (c.id === campaignId ? { ...c, status: "queued" } : c)));
      await api.post(`/email-marketing/campaigns/${campaignId}/send`);
      await fetchCampaigns();
    },
    [fetchCampaigns]
  );

  /**
   * Envia um disparo de TESTE (para a lista de teste da campanha), sem
   * afetar o status/contadores principais.
   */
  const sendTestCampaign = useCallback(
    async (campaignId) => {
      await api.post(`/email-marketing/campaigns/${campaignId}/send-test`);
      await fetchCampaigns();
    },
    [fetchCampaigns]
  );

  /**
   * Agenda a campanha para disparo automático em `scheduledAt` (ISO string).
   */
  const scheduleCampaign = useCallback(
    async (campaignId, scheduledAt) => {
      await api.post(`/email-marketing/campaigns/${campaignId}/schedule`, { scheduledAt });
      await fetchCampaigns();
    },
    [fetchCampaigns]
  );

  /**
   * Cancela o agendamento, voltando a campanha para rascunho.
   */
  const unscheduleCampaign = useCallback(
    async (campaignId) => {
      await api.post(`/email-marketing/campaigns/${campaignId}/unschedule`);
      await fetchCampaigns();
    },
    [fetchCampaigns]
  );

  /**
   * Busca o HTML do template de uma campanha já salva (usado no preview
   * quando o conteúdo não está mais em memória, ex.: ao reabrir a tela).
   */
  const fetchCampaignTemplate = useCallback(async (campaignId) => {
    const res = await api.get(`/email-marketing/campaigns/${campaignId}/template`);
    return res.data.data?.htmlContent || "";
  }, []);

  /**
   * Busca os eventos de tracking (abertura/clique) de uma campanha,
   * agrupados por contato — usado para exibir "quem abriu" no detalhe.
   */
  const fetchCampaignEvents = useCallback(async (campaignId) => {
    const res = await api.get(`/email-marketing/campaigns/${campaignId}/events`);
    return res.data.data || { events: [], byContact: [] };
  }, []);

  const getListContactsCount = useCallback(
    (listId) =>
      contacts.filter((c) => c.status === "active" && (c.listIds || []).includes(listId)).length,
    [contacts]
  );

  return {
    loading,
    campaigns,
    contacts,
    lists,
    createList,
    deleteList,
    addContact,
    removeContact,
    removeContacts,
    importContacts,
    createCampaign,
    updateCampaign,
    deleteCampaign,
    sendCampaign,
    sendTestCampaign,
    scheduleCampaign,
    unscheduleCampaign,
    fetchCampaignTemplate,
    fetchCampaignEvents,
    getListContactsCount,
  };
}
